import {
  NOTIFICATION_EVENT_KEYS,
  NOTIFICATION_EVENT_PRODUCT,
  NotificationProductCode,
  type NotificationEventKey,
  type NotificationPreference,
  type NotificationProductCode as NotificationProduct,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import {
  useClearNotificationStandard,
  useNotificationStandard,
  useReplaceNotificationStandard,
} from '@/features/notifications/use-notifications';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { NotificationChannelMatrix } from '@/platform/features/profile/profile-notifications-preferences';

const STANDARD_GROUPS = Object.values(NotificationProductCode)
  .map((productCode) => ({
    productCode,
    eventKeys: NOTIFICATION_EVENT_KEYS.filter(
      (eventKey) => NOTIFICATION_EVENT_PRODUCT[eventKey] === productCode,
    ),
  }))
  .filter((group) => group.eventKeys.length > 0);

export function NotificationStandardAdminPage() {
  usePageTitle('Mitteilungs-Standard');
  const standard = useNotificationStandard();
  const save = useReplaceNotificationStandard();
  const clear = useClearNotificationStandard();
  const [draft, setDraft] = useState<NotificationPreference[] | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const items = draft ?? standard.data?.items ?? [];

  function patchPreference(
    eventKey: NotificationEventKey,
    field: 'inApp' | 'email' | 'push',
    value: boolean,
  ) {
    setDraft((current) => {
      const base = current ?? standard.data?.items ?? [];
      return base.map((item) => {
        if (item.eventKey !== eventKey || item.channelsLocked) {
          return item;
        }
        return { ...item, [field]: value };
      });
    });
  }

  function patchProductColumn(
    productCode: NotificationProduct,
    field: 'inApp' | 'email' | 'push',
    value: boolean,
  ) {
    const keys = new Set(
      STANDARD_GROUPS.find((group) => group.productCode === productCode)?.eventKeys ?? [],
    );
    setDraft((current) => {
      const base = current ?? standard.data?.items ?? [];
      return base.map((item) => {
        if (!keys.has(item.eventKey) || item.channelsLocked) {
          return item;
        }
        return { ...item, [field]: value };
      });
    });
  }

  async function onSave(event: FormEvent) {
    event.preventDefault();
    if (items.length === 0) {
      return;
    }
    try {
      await save.mutateAsync({ items });
      setDraft(null);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Mitteilungs-Standard konnte nicht gespeichert werden.',
      );
    }
  }

  async function confirmClear() {
    try {
      await clear.mutateAsync();
      setDraft(null);
      setConfirmOpen(false);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Mitteilungs-Standard konnte nicht zurückgenommen werden.',
      );
    }
  }

  return (
    <section className="w-full min-w-0 space-y-6">
      <PlatformPageHeader
        title="Mitteilungs-Standard"
        description="Vorgabe für Glocke, E-Mail und Push. Gilt für alle, die selbst nichts gespeichert haben."
      />
      {standard.isPending ? (
        <p>Mitteilungs-Standard wird geladen …</p>
      ) : standard.isError || !standard.data ? (
        <p>Der Mitteilungs-Standard konnte nicht geladen werden.</p>
      ) : (
        <form className="space-y-6" onSubmit={(event) => void onSave(event)}>
          <Card>
            <CardHeader>
              <CardTitle>Mitteilungen</CardTitle>
            </CardHeader>
            <CardContent>
              <NotificationChannelMatrix
                groups={STANDARD_GROUPS}
                items={items}
                emailColumnDisabled={false}
                showPresets={false}
                onPreset={() => undefined}
                onColumn={patchProductColumn}
                onPatch={patchPreference}
              />
            </CardContent>
          </Card>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={save.isPending || items.length === 0}>
              {save.isPending ? <Loader2Icon className="animate-spin" aria-hidden /> : null}
              Speichern
            </Button>
            {standard.data.saved ? (
              <Button type="button" variant="outline" onClick={() => setConfirmOpen(true)}>
                Vorlage zurücknehmen
              </Button>
            ) : null}
          </div>
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Vorlage zurücknehmen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Personen ohne eigene Mitteilungen folgen danach wieder den Vorgaben der Software.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  disabled={clear.isPending}
                  onClick={(event) => {
                    event.preventDefault();
                    void confirmClear();
                  }}
                >
                  Vorlage zurücknehmen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </form>
      )}
    </section>
  );
}
